import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';

import { CreateTaskJobStageSelect_JobFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

type Fragments = {
  job: CreateTaskJobStageSelect_JobFragment;
};

type Props = {
  selectedJobStageId: string;
  applicationJobStageId: string;
  onChange: (selectedJobStageId: string) => void;
};

/**
 * This component renders a dropdown select menu for job stages that tasks can be created on.
 */
const CreateTaskJobStageSelect: FCWithFragments<Fragments, Props> = ({
  job,
  selectedJobStageId,
  applicationJobStageId,
  onChange,
}) => {
  const { t } = useTranslation();
  const jobStageOptions = (job?.jobStages || []).map((jobStage) => ({
    id: jobStage.id,
    label: jobStage.name,
    caption: jobStage.id === applicationJobStageId ? t('hiring-dashboard-tasks:tasks.create.currentStage') : undefined,
  }));

  const handleChange = (event: SelectChangeEvent) => {
    if (!event.target.value) {
      return;
    }
    onChange(event.target.value);
  };

  return (
    <Select
      displayEmpty
      fullWidth
      value={selectedJobStageId}
      renderValue={(value) =>
        jobStageOptions.find((option) => option.id === value)?.label ||
        t('hiring-dashboard-tasks:tasks.create.selectStage')
      }
      onChange={handleChange}
    >
      {jobStageOptions.map((option) => {
        return (
          <MenuItem
            sx={
              // If there is a caption, render as a vertical stack
              option.caption
                ? {
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: 0.5,
                  }
                : {}
            }
            key={option.id}
            value={option.id}
          >
            {option.label}
            {option.caption && <Typography variant="subtitle2">{option.caption}</Typography>}
          </MenuItem>
        );
      })}
    </Select>
  );
};

CreateTaskJobStageSelect.fragments = {
  job: gql`
    fragment CreateTaskJobStageSelect_job on Job {
      id
      jobStages {
        id
        name
      }
    }
  `,
};

export default CreateTaskJobStageSelect;
